import { Inject, Injectable } from '@angular/core';
import { DateRange } from '@vendasta/uikit';
import { switchMap } from 'rxjs/operators';
import {
  ClientOrigin,
  CSVExports,
  CSVFormatOptions,
  DateRangeFilter,
  ListOpportunitiesRequestFilters,
  ListOpportunitiesRequestSortField,
  ListOpportunitiesRequestSortOption,
  OpportunityRevenuePeriod,
  RevenuePeriodFilter,
  SalesOpportunitiesService,
  SortDirection,
} from '@vendasta/sales-opportunities';
import { combineLatest, Observable } from 'rxjs';
import { PipelineField, PipelineSort } from '../../../../pipelines/pipeline-sort';
import { PipelineStatuses } from '../../../pipeline-status-checkbox/pipeline-status-checkbox.service';
import { PipelineTableFilters } from '../../../../filters/filters-service/pipeline-filters.service';
import { PipelineTableService } from '../../../pipeline-table-service/pipeline-table.service';
import { DEFAULT_CURRENCY_CODE_TOKEN } from '../../../tokens';
import { MARKET_ID_TOKEN, PARTNER_ID_TOKEN } from '../../../../filters/tag-selector/tokens';
import { CLIENT_ORIGIN_TOKEN } from './tokens';

@Injectable()
export class PipelineExportCsvService {
  readonly currency$ = this.defaultCurrency$;

  constructor(
    private readonly pipelineTableService: PipelineTableService,
    private readonly salesOpportunitiesService: SalesOpportunitiesService,
    @Inject(PARTNER_ID_TOKEN) private readonly partnerId$: Observable<string>,
    @Inject(MARKET_ID_TOKEN) private readonly marketId$: Observable<string>,
    @Inject(DEFAULT_CURRENCY_CODE_TOKEN) private readonly defaultCurrency$: Observable<string>,
    @Inject(CLIENT_ORIGIN_TOKEN) private readonly clientOrigin: ClientOrigin,
  ) {}

  public static getListFilters(filters: PipelineTableFilters): ListOpportunitiesRequestFilters {
    if (!filters) {
      return new ListOpportunitiesRequestFilters();
    }

    const listFilters = new ListOpportunitiesRequestFilters();
    if (filters.salespeopleIds) {
      listFilters.salesPersonIds = filters.salespeopleIds;
    }

    if (filters.statuses) {
      listFilters.pipelineStages = PipelineExportCsvService.getPipelinesStatuses(filters.statuses);
    }

    const createdDateRange = PipelineExportCsvService.buildDateRange(filters.createdDate);
    const expectedCloseRange = PipelineExportCsvService.buildDateRange(filters.expectedClose);
    const actualCloseRange = PipelineExportCsvService.buildDateRange(filters.actualClose);
    const assignedDateRange = PipelineExportCsvService.buildDateRange(filters.assignedDate);

    if (createdDateRange) {
      listFilters.createdDateFilter = createdDateRange;
    }

    if (expectedCloseRange) {
      listFilters.expectedCloseDateFilter = expectedCloseRange;
    }

    if (actualCloseRange) {
      listFilters.actualCloseDateFilter = actualCloseRange;
    }

    if (assignedDateRange) {
      listFilters.assignedDate = assignedDateRange;
    }

    if (filters.packages) {
      listFilters.packageIds = filters.packages.map((p) => p.packageId);
    }

    if (filters.oppTags) {
      listFilters.tags = (listFilters.tags ?? []).concat(filters.oppTags);
    }

    if (filters.acctTags) {
      listFilters.tags = (listFilters.tags ?? []).concat(filters.acctTags);
    }

    if (filters.pipelines) {
      listFilters.pipelineIds = filters.pipelines.map((p) => p.id);
    }
    if (listFilters.pipelineIds?.length === 1) {
      if (filters.pipelineStage) {
        const probs = filters.pipelines[0].findProbabilityRangeFromStage(filters.pipelineStage);
        listFilters.probabilityRangeFilter = {
          maximum: probs.maximum,
          minimum: probs.minimum,
          // DISCLAIMER: DON'T DO THIS AT HOME (thanks copilot)
          // I mean this type assertion only works because the typescript SDKs will handle this for us.
          // But it's not a good idea to do this in your own code as the funtions not implemented may throw errors.
        } as unknown as ListOpportunitiesRequestFilters['probabilityRangeFilter']; // TODO: export ProbabilityRangeFilter from sales-opportunities
      }
    }

    if (filters.products) {
      listFilters.lineItemProductIds = filters.products.map((p) => p.productId);
    }

    if (filters.monthlyRevenueDate) {
      const revenue = new RevenuePeriodFilter({
        revenueDate: filters.monthlyRevenueDate,
        revenuePeriod: OpportunityRevenuePeriod.SELECTED_MONTH,
      });
      listFilters.revenuePeriodFilter = revenue;
    }

    if (filters.types) {
      listFilters.opportunityTypes = filters.types.map((type) => type.id);
    }

    return listFilters;
  }

  public static buildDateRange(inputDateRange: DateRange): DateRangeFilter {
    let returnDateRange: DateRangeFilter;
    if (!!inputDateRange && (!!inputDateRange.start || !!inputDateRange.end)) {
      returnDateRange = new DateRangeFilter();
      if (inputDateRange?.start) {
        returnDateRange.beginRange = inputDateRange.start;
      }
      if (inputDateRange?.end) {
        returnDateRange.endRange = inputDateRange.end;
      }
    } else {
      return null;
    }
    return returnDateRange;
  }

  public static getPipelinesStatuses(pipelineStatuses: PipelineStatuses): string[] {
    const statuses: string[] = [];
    if (!pipelineStatuses) {
      return [];
    }

    if (pipelineStatuses.open === true) {
      statuses.push('open');
    }

    if (pipelineStatuses.won === true) {
      statuses.push('closed-won');
    }

    if (pipelineStatuses.lost === true) {
      statuses.push('closed-lost');
    }
    return statuses;
  }

  public static getSortOption(sort: PipelineSort): ListOpportunitiesRequestSortOption {
    const sortOption = new ListOpportunitiesRequestSortOption();
    switch (sort.active) {
      case PipelineField.POTENTIAL: {
        sortOption.field = ListOpportunitiesRequestSortField.PROJECTED_FIRST_YEAR_VALUE;
        break;
      }

      case PipelineField.PIPELINE_STAGE: {
        sortOption.field = ListOpportunitiesRequestSortField.PROBABILITY;
        break;
      }

      case PipelineField.CREATED: {
        sortOption.field = ListOpportunitiesRequestSortField.CREATED;
        break;
      }

      case PipelineField.EXPECTED_CLOSE: {
        sortOption.field = ListOpportunitiesRequestSortField.EXPECTED_CLOSE;
        break;
      }

      case PipelineField.ASSIGNED_DATE: {
        sortOption.field = ListOpportunitiesRequestSortField.ASSIGNED_DATE;
        break;
      }

      case PipelineField.ACCOUNT: {
        sortOption.field = ListOpportunitiesRequestSortField.NAME;
        break;
      }

      case PipelineField.LAST_ACTIVITY: {
        sortOption.field = ListOpportunitiesRequestSortField.LAST_ACTIVITY;
        break;
      }

      case PipelineField.LAST_CONNECTED: {
        sortOption.field = ListOpportunitiesRequestSortField.LAST_CONNECTED;
        break;
      }

      case PipelineField.ACTUAL_CLOSE: {
        sortOption.field = ListOpportunitiesRequestSortField.ACTUAL_CLOSE;
        break;
      }

      case PipelineField.STATUS: {
        sortOption.field = ListOpportunitiesRequestSortField.PIPELINE_STAGE;
        break;
      }

      default: {
        sortOption.field = ListOpportunitiesRequestSortField.NAME;
        break;
      }
    }

    if (sort.isAscending === undefined || sort.isAscending === null || sort.isAscending === true) {
      sortOption.direction = SortDirection.ASCENDING;
    } else {
      sortOption.direction = SortDirection.DESCENDING;
    }

    return sortOption;
  }

  initiateCSVExport(csvExports: CSVExports): Observable<string> {
    return combineLatest([
      this.partnerId$,
      this.marketId$,
      this.pipelineTableService.selectedFilters$,
      this.pipelineTableService.sort$,
      this.pipelineTableService.lastSearchTerm$,
      this.currency$,
    ]).pipe(
      switchMap(([partnerId, marketId, pipelineFilters, sort, searchTerm, currency]) => {
        const listFilters = PipelineExportCsvService.getListFilters(pipelineFilters);
        const pipelineSort = PipelineExportCsvService.getSortOption(sort);

        const csvOptions = new CSVFormatOptions({
          currencyCode: currency,
        });

        return this.salesOpportunitiesService.initiateOpportunityExport(
          partnerId,
          marketId,
          listFilters,
          pipelineSort,
          searchTerm,
          csvExports,
          csvOptions,
          this.clientOrigin,
        );
      }),
    );
  }
}
